import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '@/shared/hooks/useUser';
import { projectTeamApi } from '../api/api';
import type {
  ProjectTeamData,
  AddInternalUserFormData,
  InviteInternalUserFormData,
  InviteExternalUserFormData,
  TeamMemberRow,
  User,
} from '../types/types';

export const useProjectTeam = () => {
  const { clientName, projectName } = useParams<{ clientName: string; projectName: string }>();
  const { user: currentUser } = useUser();
  
  const [data, setData] = useState<ProjectTeamData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formType, setFormType] = useState<'internal' | 'external'>('internal');

  // Form states
  const [addInternalUserForm, setAddInternalUserForm] = useState<AddInternalUserFormData>({
    user_id: '',
    role: '',
  });

  const [inviteInternalUserForm, setInviteInternalUserForm] = useState<InviteInternalUserFormData>({
    email: '',
    role: '',
  });

  const [inviteExternalUserForm, setInviteExternalUserForm] = useState<InviteExternalUserFormData>({
    role: '',
    external_email: '',
    external_company_name: '',
    can_access_asbuilds: false,
    can_access_progress: false,
    can_access_drones: false,
    can_access_drone_photos: false,
    can_access_surveillance: false,
  });

  // Selected users for deletion
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  // Available users for adding
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Load project team
  const loadProjectTeam = useCallback(async () => {
    if (!clientName || !projectName) return;

    setIsLoading(true);
    setError(null);

    try {
      const teamData = await projectTeamApi.getProjectTeam(clientName, projectName);
      setData(teamData);
    } catch (err: any) {
      console.error('Error loading project team:', err);
      setError(err.message || 'Failed to load project team');
    } finally {
      setIsLoading(false);
    }
  }, [clientName, projectName]);

  // Load available users when modal opens
  const loadAvailableUsers = useCallback(async () => {
    if (!data?.client.id || !data?.project.id) return;

    setLoadingUsers(true);
    try {
      const response = await projectTeamApi.getClientUsers(data.client.id, data.project.id);
      setAvailableUsers(response.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [data?.client.id, data?.project.id]);

  useEffect(() => {
    loadProjectTeam();
  }, [loadProjectTeam]);

  useEffect(() => {
    if (isAddUserModalOpen && formType === 'internal') {
      loadAvailableUsers();
    }
  }, [isAddUserModalOpen, formType, loadAvailableUsers]);

  // Add internal user
  const handleAddInternalUser = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.project.id) return;

    try {
      await projectTeamApi.addUserToProject({
        project_id: data.project.id,
        user_id: Number(addInternalUserForm.user_id),
        role: addInternalUserForm.role,
        client_name: currentUser?.is_staff ? clientName : undefined,
      });

      setSuccess('User added successfully');
      setIsAddUserModalOpen(false);
      setAddInternalUserForm({ user_id: '', role: '' });
      await loadProjectTeam();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to add user');
    }
  }, [data, addInternalUserForm, clientName, currentUser, loadProjectTeam]);

  // Invite internal user
  const handleInviteInternalUser = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.project.id) return;

    try {
      const result = await projectTeamApi.inviteInternalUser({
        project_id: data.project.id,
        email: inviteInternalUserForm.email,
        role: inviteInternalUserForm.role,
        client_name: currentUser?.is_staff ? clientName : undefined,
      });

      if (result.success) {
        setSuccess('Invitation sent successfully');
        setIsAddUserModalOpen(false);
        setInviteInternalUserForm({ email: '', role: '' });
        await loadProjectTeam();
      } else {
        setError(result.error || 'Failed to send invitation');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send invitation');
    }
  }, [data, inviteInternalUserForm, clientName, currentUser, loadProjectTeam]);

  // Invite external user
  const handleInviteExternalUser = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data?.project.id) return;

    try {
      await projectTeamApi.inviteExternalUser({
        project_id: data.project.id,
        external_email: inviteExternalUserForm.external_email,
        external_company_name: inviteExternalUserForm.external_company_name,
        role: inviteExternalUserForm.role,
        client_name: currentUser?.is_staff ? clientName : undefined,
        can_access_asbuilds: inviteExternalUserForm.can_access_asbuilds,
        can_access_progress: inviteExternalUserForm.can_access_progress,
        can_access_drones: inviteExternalUserForm.can_access_drones,
        can_access_drone_photos: inviteExternalUserForm.can_access_drone_photos,
        can_access_surveillance: inviteExternalUserForm.can_access_surveillance,
      });

      setSuccess('External user invited successfully');
      setIsAddUserModalOpen(false);
      setInviteExternalUserForm({
        role: '',
        external_email: '',
        external_company_name: '',
        can_access_asbuilds: false,
        can_access_progress: false,
        can_access_drones: false,
        can_access_drone_photos: false,
        can_access_surveillance: false,
      });
      await loadProjectTeam();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to invite external user');
    }
  }, [data, inviteExternalUserForm, clientName, currentUser, loadProjectTeam]);

  // Delete selected users
  const handleDeleteUsers = useCallback(async () => {
    if (!data?.project.id || selectedUsers.size === 0) return;

    try {
      const records = Array.from(selectedUsers).map(key => {
        const [type, id] = key.split('-');
        return {
          type: type as 'project_user' | 'invited_user',
          id: Number(id),
        };
      });

      await projectTeamApi.deleteUsers({
        project_id: data.project.id,
        records,
      });

      setSuccess('Users deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedUsers(new Set());
      await loadProjectTeam();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete users');
    }
  }, [data, selectedUsers, loadProjectTeam]);

  // Get all team members as rows
  const teamMemberRows: TeamMemberRow[] = useMemo(() => {
    if (!data) return [];

    const rows: TeamMemberRow[] = [];

    // Add project users
    data.project_users.forEach(pu => {
      rows.push({
        id: pu.id,
        type: 'project_user',
        role: pu.role,
        name: `${pu.user.first_name} ${pu.user.last_name}`,
        email: pu.user.email,
        phone: pu.user.phone_number || '',
        isPending: false,
      });
    });

    // Add invited users
    data.invited_users.forEach(iu => {
      rows.push({
        id: iu.id,
        type: 'invited_user',
        role: iu.role,
        name: iu.user
          ? `${iu.user.first_name} ${iu.user.last_name}`
          : 'Invited User (Pending)',
        email: iu.email,
        phone: iu.user?.phone_number || 'N/A',
        isPending: !iu.user,
      });
    });

    return rows;
  }, [data]);

  // Filter rows
  const filteredRows = useMemo(() => {
    let filtered = teamMemberRows;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        row =>
          row.name.toLowerCase().includes(query) ||
          row.email.toLowerCase().includes(query) ||
          row.role.toLowerCase().includes(query)
      );
    }

    if (roleFilter) {
      filtered = filtered.filter(row => row.role === roleFilter);
    }

    return filtered;
  }, [teamMemberRows, searchQuery, roleFilter]);

  // Get unique roles
  const uniqueRoles = useMemo(() => {
    const roles = new Set(teamMemberRows.map(row => row.role));
    return Array.from(roles).sort();
  }, [teamMemberRows]);

  // Toggle user selection
  const toggleUserSelection = useCallback((key: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // Toggle all users
  const toggleAllUsers = useCallback(() => {
    if (selectedUsers.size === filteredRows.length) {
      setSelectedUsers(new Set());
    } else {
      const allKeys = filteredRows.map(row => `${row.type}-${row.id}`);
      setSelectedUsers(new Set(allKeys));
    }
  }, [filteredRows, selectedUsers.size]);

  return {
    data,
    isLoading,
    error,
    success,
    setError,
    setSuccess,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    formType,
    setFormType,
    addInternalUserForm,
    setAddInternalUserForm,
    inviteInternalUserForm,
    setInviteInternalUserForm,
    inviteExternalUserForm,
    setInviteExternalUserForm,
    handleAddInternalUser,
    handleInviteInternalUser,
    handleInviteExternalUser,
    handleDeleteUsers,
    selectedUsers,
    toggleUserSelection,
    toggleAllUsers,
    teamMemberRows: filteredRows,
    uniqueRoles,
    searchQuery,
    setSearchQuery,
    roleFilter,
    setRoleFilter,
    availableUsers,
    loadingUsers,
    refetch: loadProjectTeam,
  };
};

